



























import {
  defineComponent,
  ref,
  computed,
} from '@vue/composition-api';
import type { PropType } from '@vue/composition-api';

import type { ContractListResponse } from '@/widgets/contract-view/type';
import { ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import { dcCreditSceneEnum } from '@/api/preApplyChoose/types';

import BothContract from './components/bothContract/index.vue';
import FqContract from './components/fqContract/index.vue';
import DcContract from './components/dcContract/index.vue';

export default defineComponent({
  name: 'MiddleIncomeContract',
  components: {
    BothContract,
    FqContract,
    DcContract,
  },
  props: {
    /** 分期商城产品ID列表 */
    shoppingMallCommoditys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** 贷超产品ID列表 */
    overLoanCommoditys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** 贷超业务场景 */
    scene: {
      type: String as PropType<dcCreditSceneEnum>,
      default: dcCreditSceneEnum.NO_CHANNEL_AVAILABLE,
    },
    /** 商城投放来源 */
    from: {
      type: String,
      default: '',
    },
    /** 申请模式 */
    applyMode: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    ubtSetupModuleExpose('finance_middle_income_contract_expose', {});

    /** 分期商城渠道列表 */
    const fqChannelList = ref<ContractListResponse[]>([]);

    /** 贷超渠道列表 */
    const dcChannelList = ref<ContractListResponse[]>([]);

    /** 判断渠道类型 */
    const hasBothChannels = computed(
      () => fqChannelList.value.length > 0 && dcChannelList.value.length > 0,
    );
    const hasOnlyFqChannels = computed(
      () => fqChannelList.value.length > 0 && dcChannelList.value.length === 0,
    );
    const hasOnlyDcChannels = computed(
      () => fqChannelList.value.length === 0 && dcChannelList.value.length > 0,
    );

    /** Mock 基础渠道数据（用于开发调试） */
    const mockChannelList = ref<ContractListResponse[]>([
      {
        productId: '8180',
        productName: '臻有钱',
        productIcon: 'https://hello-finance.oss-cn-hangzhou.aliyuncs.com/pro/commodity/icon/6070-1639463742695.png',
        contractList: [
          {
            name: '《借款合同》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=zhenyouqian',
          },
          {
            name: '《个人信息授权书》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=personal',
          },
          {
            name: '《征信查询授权书》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=credit',
          },
        ],
      },
      {
        productId: '8181',
        productName: '中银消费金融',
        productIcon: 'https://hello-finance.oss-cn-hangzhou.aliyuncs.com/fat/commodity/icon/8070-1659670007940.png',
        contractList: [
          {
            name: '《个人借款合同》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=zhongyin_loan',
          },
          {
            name: '《个人信息处理授权书》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=zhongyin_info',
          },
        ],
      },
      {
        productId: '8182',
        productName: '马上消费金融',
        productIcon: 'https://hello-finance.oss-cn-hangzhou.aliyuncs.com/fat/commodity/icon/8060-1658815863003.png',
        contractList: [
          {
            name: '《借款合同》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=mashang_loan',
          },
          {
            name: '《个人信息使用授权书》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=mashang_auth',
          },
          {
            name: '《征信授权书》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=mashang_credit',
          },
        ],
      },
      {
        productId: '8183',
        productName: '招联消费金融',
        productIcon: 'https://static.hellobike.com/everyimg/p/fin-loan-h5/icon_zhaolian@2x.png',
        contractList: [
          {
            name: '《借款服务合同》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=zhaolian_loan',
          },
          {
            name: '《个人信息处理授权书》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=zhaolian_info',
          },
        ],
      },
      {
        productId: '8184',
        productName: '兴业消费金融',
        productIcon: 'https://static.hellobike.com/everyimg/p/fin-loan-h5/icon_xingye@2x.png',
        contractList: [
          {
            name: '《个人贷款合同》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=xingye_loan',
          },
          {
            name: '《个人信息授权书》',
            url: 'https://m.hellobike.com/loan-finance-h5/index.html?#/contract?type=xingye_auth',
          },
        ],
      },
    ]);

    /** 开发环境：如果没有传入产品ID，使用 mock 数据 */
    fqChannelList.value = mockChannelList.value;
    dcChannelList.value = mockChannelList.value;

    /**
     * 检查是否有协议数据
     */
    const hasAnyContracts = () => fqChannelList.value.length > 0 || dcChannelList.value.length > 0;

    const getPageData = async () => {
      try {
        // TODO: 根据产品ID列表请求协议数据
        // 示例：
        // if (props.shoppingMallCommoditys.length > 0) {
        //   const fqContracts = await fetchContractsByProductIds(props.shoppingMallCommoditys);
        //   fqChannelList.value = fqContracts;
        // }
        // if (props.overLoanCommoditys.length > 0) {
        //   const dcContracts = await fetchContractsByProductIds(props.overLoanCommoditys);
        //   dcChannelList.value = dcContracts;
        // }

        // TODO: 请求资料完整度数据
        // 示例：
        // await fetchUserProfileProgress();

        // 请求完成后，检查是否有协议数据
        if (!hasAnyContracts()) {
          // 如果没有任何协议数据
        }
      } catch (error) {
        // 接口异常时
      }
    };

    getPageData();

    return {
      fqChannelList,
      dcChannelList,
      hasBothChannels,
      hasOnlyFqChannels,
      hasOnlyDcChannels,
    };
  },
});
